import { ABILITIES, ATTACK_STRENGTHS } from "@/app/characters";
import items from "@/app/items";
import { SPELLS, getEffectiveSpellProps } from "@/app/spells";
import { SPECIALS } from "@/app/specials";
import { prisma } from "@/lib/prisma";

import {
  calculateMaxHealth,
  calculateMaxStamina,
  calculateRequiredExperience,
  applyItemBenefits,
  getMindControlledOriginalPlayerId,
  removeMindControlledState,
  UNIT_STATS,
} from "@/app/characters";

import { getFortressBonuses } from "@/app/upgrades";

function getPlayerColor(index) {
  // Return a tailwind color based on index
  const colors = [
    "bg-red-500",
    "bg-blue-500",
    "bg-yellow-500",
    "bg-pink-500",
    "bg-purple-500",
    "bg-indigo-500",
  ];

  return colors[index % colors.length];
}

function getPlayerTextColor(index) {
  // Return a tailwind color based on index
  const colors = [
    "text-red-500",
    "text-blue-500",
    "text-yellow-500",
    "text-pink-500",
    "text-purple-500",
    "text-indigo-500",
  ];

  return colors[index % colors.length];
}

function getCharacterTypeIcon(type) {
  if (!type) return "";
  return `/${type}.png`;
}

function getCharacterIconSrc(character) {
  if (!character) return "";
  return character.portraitPath || getCharacterTypeIcon(character.type);
}

function getNameOrFortress(char, unlockedUpgrades = [], maxLength = 15) {
  if (!char) {
    return "Unknown";
  }

  if (char.type === "fortress") {
    if (unlockedUpgrades.includes("FORTRESS_3")) {
      return "Fortress III";
    } else if (unlockedUpgrades.includes("FORTRESS_2")) {
      return "Fortress II";
    } else {
      return "Fortress";
    }
  } else {
    if (char.name.length > maxLength) {
      return char.name.slice(0, maxLength) + "...";
    } else {
      return char.name;
    }
  }
}

// Centralized transaction executor for consistent error handling
async function executeTransaction(transactionFn) {
  try {
    return await prisma.$transaction(transactionFn, {
      timeout: 15000, // Increase timeout to 15 seconds
    });
  } catch (error) {
    console.error("Transaction error:", error);
    throw error;
  }
}

// Find item by ability type
function findItemByAbility(abilityType) {
  return items.find(
    (item) => item.benefits && item.benefits.ability === abilityType
  );
}

// Pay stamina for an action
async function payStamina(tx, character, action, strength = null) {
  let staminaCost = ABILITIES[action].stamina;

  // For ability actions, check if there's a specific item cost
  if (["generateGold", "refreshStamina", "grantExperience"].includes(action)) {
    const abilityItem = findItemByAbility(action);
    if (abilityItem?.benefits?.staminaCost) {
      staminaCost = abilityItem.benefits.staminaCost;
    }
  }

  // Apply strength modifier for attack/shoot actions
  if ((action === "attack" || action === "shoot") && strength) {
    const strengthConfig = ATTACK_STRENGTHS[strength];
    if (strengthConfig) {
      staminaCost += strengthConfig.staminaModifier;
    }
  }

  if (character.stamina < staminaCost) {
    throw new Error(
      `Not enough stamina, ${character.name} needs ${staminaCost} stamina but has ${character.stamina}`
    );
  }

  character.stamina -= staminaCost;

  // Save character's new stamina
  await tx.character.update({
    where: { id: character.id },
    data: { stamina: character.stamina },
  });

  return character;
}

// Calculate damage based on roll and character stats
function calculateDamage(roll, character, target = null, strength = null) {
  if (roll <= 1) return 0; // Critical miss = 0 damage

  // Apply item benefits to attacker
  const enhancedAttacker = applyItemBenefits(character, items);

  // Calculate base max damage potential
  let maxDamagePotential = enhancedAttacker.attack;

  // Apply strength modifier to max damage potential
  if (strength) {
    const strengthConfig = ATTACK_STRENGTHS[strength];
    if (strengthConfig) {
      maxDamagePotential = Math.floor(
        maxDamagePotential * (1 + strengthConfig.damageModifier)
      );
    }
  }

  // Calculate damage based on roll (roll/6 determines percentage of max damage)
  let baseDamage = Math.floor((roll / 6) * maxDamagePotential);

  // Apply special bonuses against specific types (items with "against" property like Spear, Axe)
  if (target) {
    const attackerItems = character.items || [];

    // Get strength modifier if provided
    let strengthMultiplier = 1;
    if (strength) {
      const strengthConfig = ATTACK_STRENGTHS[strength];
      if (strengthConfig) {
        strengthMultiplier = 1 + strengthConfig.damageModifier;
      }
    }

    // Check each item for type-specific bonuses
    for (const itemName of attackerItems) {
      const item = items.find((i) => i.name === itemName);
      // Only apply conditional bonus if item has an "against" array defined
      const againstTypes = item?.benefits?.against;
      if (!againstTypes || !Array.isArray(againstTypes)) {
        continue; // Skip items without conditional targeting (like Lethal Arrows)
      }

      // Check if target matches any of the conditions
      let shouldApplyBonus = false;

      for (const condition of againstTypes) {
        // Handle negative conditions (e.g., "non-mounted" means target is NOT mounted)
        if (condition.startsWith("non-")) {
          const stateToExclude = condition.slice(4); // Remove "non-" prefix
          const targetHasState =
            target.state?.includes(stateToExclude) || false;
          if (!targetHasState) {
            shouldApplyBonus = true;
            break;
          }
        } else {
          // Standard condition: check if target type or state matches
          if (target.type === condition || target.state?.includes(condition)) {
            shouldApplyBonus = true;
            break;
          }
        }
      }

      if (shouldApplyBonus) {
        // Use conditionalDamage for items like Spear/Axe/Lance that have specific bonus damage
        const staticBonus = item.benefits.conditionalDamage || 0;
        baseDamage += Math.floor(staticBonus * strengthMultiplier);
      }
    }
  }

  return baseDamage;
}

// Award experience and handle level ups
async function awardExperience(tx, character, amount, gameId, playerId) {
  if (!character || character.type === "fortress") return;
  // Current level and experience
  const currentLevel = character.level;
  const currentExp = character.experience;

  // Calculate required experience for level up
  const requiredExp = calculateRequiredExperience(currentLevel);

  // Add new experience
  const newExp = currentExp + amount;

  // Check if character should level up
  let newLevel = currentLevel;
  let remainingExp = newExp;
  let didLevelUp = false;

  if (newExp >= requiredExp) {
    newLevel = currentLevel + 1;
    remainingExp = newExp - requiredExp;
    didLevelUp = true;

    // Calculate new max health and stamina for the next level
    const oldMaxHealth = calculateMaxHealth(character.type, currentLevel);
    const newMaxHealth = calculateMaxHealth(character.type, newLevel);
    const healthBonus = newMaxHealth - oldMaxHealth;

    const oldMaxStamina = calculateMaxStamina(character.type, currentLevel);
    const newMaxStamina = calculateMaxStamina(character.type, newLevel);
    const staminaBonus = newMaxStamina - oldMaxStamina;

    // Calculate attack bonus (8% improvement)
    const attackBonus = Math.ceil(character.attack * 0.08);

    // Calculate 5% health increase
    const healthIncrease = Math.ceil(character.health * 0.05);

    // Update character with new stats and level
    await tx.character.update({
      where: { id: character.id },
      data: {
        level: newLevel,
        experience: remainingExp,
        health: Math.min(
          character.health + healthBonus + healthIncrease,
          newMaxHealth
        ),
        stamina: Math.min(character.stamina + staminaBonus, newMaxStamina), // Ensure stamina doesn't exceed new maximum
        attack: character.attack + attackBonus,
      },
    });

    // When a character hits level 3, they earn ONE extra special ability pick.
    // We model this as a pending choice in game.state so the client can show a modal.
    if (newLevel === 3) {
      try {
        const baseStartingSpecials =
          UNIT_STATS?.[character.type]?.startingSpecials || 1;
        const maxSpecialsAtLevel3 = baseStartingSpecials + 1;
        const currentSpecialCount = (character.specials || []).length;
        const hasAnySpecialOptions =
          Array.isArray(UNIT_STATS?.[character.type]?.specials) &&
          UNIT_STATS[character.type].specials.length > 0;

        // Only prompt if the unit type actually has specials and isn't already at its new cap.
        if (hasAnySpecialOptions && currentSpecialCount < maxSpecialsAtLevel3) {
          const game = await tx.game.findUnique({ where: { id: gameId } });
          const state = Array.isArray(game?.state) ? game.state : [];

          // Remove any stale prompts for this character, then add a fresh one.
          const nextState = state
            .filter(
              (s) =>
                !(
                  s?.type === "choose_special" &&
                  s?.data?.characterId === character.id
                )
            )
            .concat([
              {
                type: "choose_special",
                data: {
                  playerId: character.playerId,
                  characterId: character.id,
                },
              },
            ]);

          await tx.game.update({
            where: { id: gameId },
            data: { state: nextState },
          });
        }
      } catch (e) {
        console.error("Failed to set choose_special state:", e);
      }
    }

    // Log the level up
    await logAction(tx, "level_up", {
      gameId,
      playerId,
      character,
      amount: newLevel,
    });
  } else {
    // Just update experience without leveling up
    await tx.character.update({
      where: { id: character.id },
      data: {
        experience: newExp,
      },
    });
  }

  return { didLevelUp, newLevel, newExp: remainingExp };
}

// Handle damage to a target
async function handleDamage(
  tx,
  target,
  damage,
  playerId,
  gameId,
  attackerId = null
) {
  // Apply target's defensive item benefits
  const enhancedTarget = applyItemBenefits(target, items);

  // Reduce damage by defense value from items
  let finalDamage = damage;
  if (enhancedTarget.defense > 0) {
    finalDamage = Math.max(0, damage - enhancedTarget.defense);
  }

  const newHealth = target.health - finalDamage;

  if (newHealth <= 0) {
    // Store target info before deletion
    const targetInfo = {
      id: target.id,
      name: target.name,
      type: target.type,
      playerId: target.playerId,
    };

    // Flag the character dead and reward gold
    const { gold, experience } = await killAndRewardGold(
      tx,
      target,
      playerId,
      gameId,
      attackerId
    );

    return { wasKilled: true, finalDamage, goldReward: gold };
  } else {
    await tx.character.update({
      where: { id: target.id },
      data: { health: newHealth },
    });
    return { wasKilled: false, finalDamage, goldReward: 0 };
  }
}

async function getNextSkeletonName(tx, playerId) {
  const count = await tx.character.count({
    where: { playerId, type: "skeleton" },
  });
  return `Skeleton ${count + 1}`;
}

async function spawnSkeleton(
  tx,
  { gameId, playerId, spawnTurn = null, spawnedBy = null, reason = "spawn" }
) {
  const skeletonStats = UNIT_STATS.skeleton;
  if (!skeletonStats) {
    throw new Error("Skeleton stats are not defined");
  }

  const name = await getNextSkeletonName(tx, playerId);
  const maxStamina = calculateMaxStamina("skeleton", 1);

  const newSkeleton = await tx.character.create({
    data: {
      name,
      type: "skeleton",
      playerId,
      level: 1,
      experience: 0,
      spawnTurn,
      health: skeletonStats.health,
      speed: skeletonStats.speed,
      range: skeletonStats.range,
      attack: skeletonStats.attack,
      stamina: maxStamina,
      state: [],
      items: [],
      specials: [],
      specialCooldowns: {},
    },
  });

  await logAction(tx, "spawn", {
    gameId,
    playerId,
    character: spawnedBy,
    spawned: newSkeleton,
    reason,
  });

  return newSkeleton;
}

// Kill a character and reward gold
async function killAndRewardGold(
  tx,
  target,
  playerId,
  gameId,
  attackerId = null
) {
  // The gold multiplier is a % of the cost of the unit
  const goldMultiplier = 0.25;

  // Soft-delete: set alive=false and record death turn, clear transient states
  // Get current game turn
  const game = await tx.game.findUnique({ where: { id: gameId } });
  const retainedState = (target.state || []).filter(
    (state) =>
      typeof state === "string" && state.startsWith("mind_controlled:")
  );
  await tx.character.update({
    where: { id: target.id },
    data: {
      alive: false,
      deathTurn: game?.turnCounter || null,
      state: retainedState,
      stamina: 0,
    },
  });

  // Remove this dead character from any opponents' combat state
  // (combat is stored as "combat:<id>" inside Character.state[])
  const combatTag = `combat:${target.id}`;
  const stillInCombatWithDead = await tx.character.findMany({
    where: { state: { has: combatTag } },
    select: { id: true, state: true },
  });
  if (stillInCombatWithDead.length > 0) {
    await Promise.all(
      stillInCombatWithDead.map((c) =>
        tx.character.update({
          where: { id: c.id },
          data: {
            state: { set: (c.state || []).filter((s) => s !== combatTag) },
          },
        })
      )
    );
  }

  // Calculate and award gold
  const goldGained = Math.round(UNIT_STATS[target.type].cost * goldMultiplier);
  const player = await tx.player.findUnique({ where: { id: playerId } });
  await tx.player.update({
    where: { id: playerId },
    data: { mana: player.mana + goldGained },
  });

  let killExpAmount = 0;

  if (target.type !== "fortress") {
    // Increment the killing player's total kill count
    await tx.player.update({
      where: { id: playerId },
      data: { kills: { increment: 1 } },
    });
  }

  let attacker = null;
  if (attackerId) {
    // Get the attacker character
    attacker = await tx.character.findUnique({
      where: { id: attackerId },
    });
  }

  if (attackerId && attacker && target.type !== "fortress") {
    // Update kill count on the specific attacker character
    await tx.character.update({
      where: { id: attackerId },
      data: { kills: { increment: 1 } },
    });

    // Award experience for the kill (more for higher cost units)
    // NOTE: Kamikaze should not level up from its own self-destruct kills.
    // It dies immediately after kamikazing, and letting it level mid-resolution
    // can cause confusing logs / unintended damage scaling.
    if (attacker && attacker.type !== "kamikaze") {
      killExpAmount = 75 + Math.floor(UNIT_STATS[target.type].cost / 3);
      await awardExperience(
        tx,
        attacker,
        killExpAmount,
        gameId,
        attacker.playerId
      );
    }
  }

  if (
    attacker &&
    attacker.type === "skeleton" &&
    target.playerId !== attacker.playerId
  ) {
    await spawnSkeleton(tx, {
      gameId,
      playerId: attacker.playerId,
      spawnTurn: game?.turnCounter || null,
      spawnedBy: attacker,
      reason: "kill",
    });
  }

  // If fortress was killed, log game over and update user stats
  if (target.type === "fortress") {
    await logAction(tx, "game_over", {
      gameId,
      playerId,
      details: {
        targetPlayerId: target.playerId,
      },
    });

    // Update user stats for both players
    const [winningPlayer, losingPlayer] = await Promise.all([
      tx.player.findUnique({ where: { id: playerId } }),
      tx.player.findUnique({ where: { id: target.playerId } }),
    ]);

    const statsUpdates = [];
    if (winningPlayer?.userId) {
      statsUpdates.push(
        tx.user.update({
          where: { id: winningPlayer.userId },
          data: {
            wins: { increment: 1 },
            gamesPlayed: { increment: 1 },
          },
        })
      );
    }
    if (losingPlayer?.userId) {
      statsUpdates.push(
        tx.user.update({
          where: { id: losingPlayer.userId },
          data: {
            losses: { increment: 1 },
            gamesPlayed: { increment: 1 },
          },
        })
      );
    }
    if (statsUpdates.length > 0) {
      await Promise.all(statsUpdates);
    }
  }
  return { gold: goldGained, experience: killExpAmount };
}

// Log game actions
async function logAction(tx, action, details) {
  const {
    gameId,
    playerId,
    character,
    target,
    targets,
    damage,
    amount,
    kind,
    itemName,
    name,
    type,
    wasKilled,
    goldReward,
    dodgeResult,
    originalDamage,
    turnCounter,
    upgradeKey,
    upgradeType,
    success,
    spawned,
    reason,
    originalPlayerId,
  } = details;

  const formattedDetails = {
    character: character && {
      id: character.id,
      name: character.name,
      type: character.type,
      playerId: character.playerId,
    },
    ...(target && {
      target: {
        id: target.id,
        name: target.name,
        type: target.type,
        playerId: target.playerId,
      },
    }),
    ...(spawned && {
      spawned: {
        id: spawned.id,
        name: spawned.name,
        type: spawned.type,
        playerId: spawned.playerId,
      },
    }),
    ...(targets && { targets }),
    ...(damage !== undefined && { damage }),
    ...(amount !== undefined && { amount }),
    ...(kind && { kind }),
    ...(itemName && { itemName }),
    ...(name && { name }),
    ...(type && { type }),
    ...(reason && { reason }),
    ...(originalPlayerId !== undefined && { originalPlayerId }),
    ...(wasKilled !== undefined && { wasKilled }),
    ...(goldReward !== undefined && { goldReward }),
    ...(dodgeResult && { dodgeResult }),
    ...(originalDamage !== undefined && { originalDamage }),
    ...(turnCounter !== undefined && { turnCounter }),
    ...(upgradeKey && { upgradeKey }),
    ...(upgradeType && { upgradeType }),
    ...(success !== undefined && { success }),
  };

  return await tx.gameAction.create({
    data: {
      gameId,
      playerId,
      action,
      details: formattedDetails,
    },
  });
}

function getIncome(game, player) {
  const baseIncreaseGoldBy = 5;
  let increaseGoldBy = Math.floor(baseIncreaseGoldBy + game.turnCounter * 0.425);

  // Add gold from fortress upgrades
  const unlockedUpgrades = player.upgrades?.map((u) => u.upgrade) || [];
  const { goldBonus } = getFortressBonuses(unlockedUpgrades);
  increaseGoldBy += goldBonus;

  return increaseGoldBy;
}

// Process poison damage for a specific player's characters at the start of their turn
async function processPoisonDamage(tx, gameId, playerId) {
  const POISON_DAMAGE = 5;

  // Find all alive characters belonging to the player whose turn is starting
  // (Prisma can't do startsWith on array elements, so we filter in JS)
  const playerCharacters = await tx.character.findMany({
    where: {
      playerId: playerId,
      alive: true,
    },
    include: {
      player: { select: { id: true } },
    },
  });

  // Filter to only those with poisoned states
  const actuallyPoisoned = playerCharacters.filter((char) =>
    (char.state || []).some((s) => s.startsWith("poisoned:"))
  );

  for (const char of actuallyPoisoned) {
    const poisonStates = (char.state || []).filter((s) =>
      s.startsWith("poisoned:")
    );

    for (const poisonState of poisonStates) {
      // Parse: poisoned:<archerId>:<turnsRemaining>
      const parts = poisonState.split(":");
      const archerId = parseInt(parts[1], 10);
      const turnsRemaining = parseInt(parts[2], 10);

      // Deal poison damage (ignores defense)
      const newHealth = char.health - POISON_DAMAGE;
      const wasKilled = newHealth <= 0;

      // Update poison state (decrement turns or remove if done)
      let newState = (char.state || []).filter((s) => s !== poisonState);
      if (turnsRemaining > 1 && !wasKilled) {
        newState.push(`poisoned:${archerId}:${turnsRemaining - 1}`);
      }

      if (wasKilled) {
        // Character died from poison
        const retainedState = (char.state || []).filter((state) =>
          state.startsWith("mind_controlled:")
        );
        await tx.character.update({
          where: { id: char.id },
          data: {
            health: 0,
            alive: false,
            state: retainedState,
            stamina: 0,
          },
        });

        // Award XP to the archer who poisoned them
        const archer = await tx.character.findUnique({
          where: { id: archerId },
        });

        if (archer && archer.alive) {
          // Award kill XP to the archer
          const killExpAmount = 75 + Math.floor(UNIT_STATS[char.type]?.cost / 3 || 0);
          await awardExperience(tx, archer, killExpAmount, gameId, archer.playerId);

          // Increment archer's kill count
          await tx.character.update({
            where: { id: archerId },
            data: { kills: { increment: 1 } },
          });

          // Increment player's kill count
          await tx.player.update({
            where: { id: archer.playerId },
            data: { kills: { increment: 1 } },
          });
        }

        // Log the poison death
        await logAction(tx, "poison_damage", {
          gameId,
          playerId: char.playerId,
          character: { id: char.id, name: char.name, type: char.type, playerId: char.playerId },
          damage: POISON_DAMAGE,
          wasKilled: true,
        });
      } else {
        // Just deal damage and update state
        await tx.character.update({
          where: { id: char.id },
          data: {
            health: newHealth,
            state: { set: newState },
          },
        });

        // Log the poison damage
        await logAction(tx, "poison_damage", {
          gameId,
          playerId: char.playerId,
          character: { id: char.id, name: char.name, type: char.type, playerId: char.playerId },
          damage: POISON_DAMAGE,
          wasKilled: false,
          turnsRemaining: turnsRemaining - 1,
        });
      }
    }
  }
}

// Update turn with regeneration and stats refresh
async function updateTurn(tx, gameId) {
  const game = await tx.game.findUnique({
    where: { id: gameId },
    select: {
      currentPlayer: true,
      turnCounter: true,
      players: {
        select: {
          id: true,
          playerNum: true,
          mana: true,
          spellCooldowns: true,
          upgrades: {
            select: {
              upgrade: true,
            },
          },
        },
      },
    },
  });

  const currentPlayer = game.currentPlayer;
  const nextPlayer = (game.currentPlayer % game.players.length) + 1;

  // Reuse player records already loaded with the game to avoid redundant DB lookups
  const playersByNum = new Map(game.players.map((p) => [p.playerNum, p]));
  const currentPlayerRecord = playersByNum.get(currentPlayer);
  const nextPlayerRecord = playersByNum.get(nextPlayer);

  if (!currentPlayerRecord || !nextPlayerRecord) {
    throw new Error(
      `updateTurn: missing player record(s) for current=${currentPlayer}, next=${nextPlayer} in game ${gameId}`
    );
  }

  const nextPlayerId = nextPlayerRecord.id;

  // Process poison damage for the player whose turn is starting
  await processPoisonDamage(tx, gameId, nextPlayerId);

  // Fetch characters for both players in a single query (fewer DB roundtrips)
  const currentPlayerId = currentPlayerRecord.id;
  const allCharacters = await tx.character.findMany({
    where: { playerId: { in: [currentPlayerId, nextPlayerId] } },
  });

  let currentPlayerCharacters = [];
  let nextPlayerCharacters = [];
  if (currentPlayerId === nextPlayerId) {
    currentPlayerCharacters = allCharacters;
    nextPlayerCharacters = allCharacters;
  } else {
    for (const char of allCharacters) {
      if (char.playerId === currentPlayerId) currentPlayerCharacters.push(char);
      else if (char.playerId === nextPlayerId) nextPlayerCharacters.push(char);
    }
  }

  // Prepare batch updates for current player characters (clear turn-based states)
  const turnStatesToClear = new Set([
    "unplanted_flag",
    "stunned",
    "meditated",
    "battle_cry_buff",
  ]);

  // Release any mind-controlled characters at end of the current player's turn.
  const releasedIds = new Set();
  const releaseUpdates = [];
  const releasedToNext = [];

  for (const char of currentPlayerCharacters) {
    const originalPlayerId = getMindControlledOriginalPlayerId(char.state || []);
    if (!originalPlayerId || originalPlayerId === currentPlayerId) continue;

    const withoutMindControl = removeMindControlledState(char.state || []);
    const clearedState = withoutMindControl.filter(
      (state) => !turnStatesToClear.has(state)
    );

    releasedIds.add(char.id);
    releaseUpdates.push(
      tx.character.update({
        where: { id: char.id },
        data: { playerId: originalPlayerId, state: clearedState },
      })
    );

    if (originalPlayerId === nextPlayerId) {
      releasedToNext.push({
        ...char,
        playerId: originalPlayerId,
        state: clearedState,
      });
    }
  }

  const currentPlayerUpdates = currentPlayerCharacters
    .filter((char) => !releasedIds.has(char.id))
    .map((char) => {
      // Fast path: nothing to clear
      if (!char.state?.some((s) => turnStatesToClear.has(s))) return null;

      // Reset turn-based states
      const newState = (char.state || []).filter(
        (state) => !turnStatesToClear.has(state)
      );

      return tx.character.update({
        where: { id: char.id },
        data: { state: newState },
      });
    })
    .filter(Boolean);

  // Reduce spell cooldowns for the next player
  let currentCooldowns = nextPlayerRecord.spellCooldowns || {};
  if (
    currentCooldowns &&
    typeof currentCooldowns === "object" &&
    !Array.isArray(currentCooldowns)
  ) {
    // Reduce all cooldowns by 1 if they're greater than 0
    const updated = { ...currentCooldowns };
    Object.keys(updated).forEach((spellId) => {
      if (updated[spellId] > 0) updated[spellId]--;
    });
    currentCooldowns = updated;
  }

  const increaseGoldBy = getIncome(game, nextPlayerRecord);

  const unlockedUpgrades =
    nextPlayerRecord.upgrades?.map((u) => u.upgrade) || [];

  // Prepare batch updates for next player characters (regeneration)
  const nextPlayerCharacterUpdates = [
    ...nextPlayerCharacters,
    ...releasedToNext,
  ].map((char) => {
    // Calculate character's actual max stats based on level and upgrades
    const maxStamina = calculateMaxStamina(char.type, char.level);

    // Refresh stamina
    const regenRate = 45 + char.stamina / 3;
    const newStamina = Math.min(char.stamina + regenRate, maxStamina);

    // Tick down special cooldowns only if the character has specials
    let specialCooldownUpdate = {};
    if (char.specials && char.specials.length > 0) {
      const current = char.specialCooldowns || {};
      if (current && typeof current === "object" && !Array.isArray(current)) {
        const updated = { ...current };
        let changed = false;
        Object.keys(updated).forEach((sid) => {
          if (updated[sid] > 0) {
            updated[sid]--;
            changed = true;
          }
        });
        if (changed) specialCooldownUpdate = { specialCooldowns: updated };
      }
    }

    // Remove defensive state only if present (skip allocations on the common case)
    let stateUpdate = {};
    if (char.state && char.state.includes("aura_of_protection")) {
      stateUpdate = {
        state: char.state.filter((state) => state !== "aura_of_protection"),
      };
    }

    return tx.character.update({
      where: { id: char.id },
      data: {
        stamina: newStamina,
        ...specialCooldownUpdate,
        ...stateUpdate,
      },
    });
  });

  // Execute all updates in parallel: current player state updates, next player regeneration, player update, and game update
  await Promise.all([
    ...currentPlayerUpdates,
    ...releaseUpdates,
    ...nextPlayerCharacterUpdates,
    tx.player.update({
      where: { id: nextPlayerId },
      data: {
        mana: { increment: increaseGoldBy },
        spellCooldowns: currentCooldowns,
      },
    }),
    tx.game.update({
      where: { id: gameId },
      data: {
        currentPlayer: nextPlayer,
        turnCounter: { increment: 1 },
      },
    }),
  ]);

  // Update the game state
  return increaseGoldBy;
}

// Apply cooldown to a spell
async function applyCooldown(tx, playerId, spellKey) {
  const player = await tx.player.findUnique({
    where: { id: playerId },
    include: { upgrades: true },
  });

  const unlockedKeys = (player?.upgrades || []).map((u) => u.upgrade);
  const effective = getEffectiveSpellProps(spellKey, unlockedKeys);

  let currentCooldowns = player.spellCooldowns || {};
  currentCooldowns[spellKey] = effective.cooldown;

  await tx.player.update({
    where: { id: playerId },
    data: { spellCooldowns: currentCooldowns },
  });
}

function playSound(name) {
  const audio = new Audio(`/sounds/${name}.mp3`);
  audio.volume = 0.7;
  audio.play();
}

function playDiceRollSound(roll) {
  if (roll >= 1 && roll <= 6) {
    playSound(roll.toString());
  }
}

// Helper component for game log and toasts
export const PlayerColorSquare = ({ playerId }) => (
  <div
    className={`w-3 h-3 rounded-sm inline-block mr-1 ${getPlayerColor(
      playerId
    )}`}
  />
);

// Helper component for game log and toasts
export const CharacterIcon = ({ character, size = "w-7 h-7" }) => {
  const src = getCharacterIconSrc(character);

  return (
    <img
      src={src}
      alt={character?.name || character?.type || "character"}
      className={`${size} inline mr-[3px] align-middle object-cover rounded`}
    />
  );
};

function shortenString(str, maxLength) {
  if (str.length > maxLength) {
    return str.slice(0, maxLength) + "...";
  } else {
    return str;
  }
}

async function resetSpecialCooldown(tx, characterId, specialId) {
  const cooldownNumber = SPECIALS[specialId].cooldown;
  // Fetch current cooldowns to avoid overwriting other specials (e.g., wizards can have two)
  const current = await tx.character.findUnique({
    where: { id: characterId },
    select: { specialCooldowns: true },
  });

  const mergedCooldowns = {
    ...(current?.specialCooldowns || {}),
    [specialId]: cooldownNumber,
  };

  await tx.character.update({
    where: { id: characterId },
    data: { specialCooldowns: mergedCooldowns },
  });
}

export {
  getPlayerColor,
  getPlayerTextColor,
  getCharacterTypeIcon,
  getCharacterIconSrc,
  getNameOrFortress,
  shortenString,
  executeTransaction,
  findItemByAbility,
  payStamina,
  calculateDamage,
  awardExperience,
  handleDamage,
  killAndRewardGold,
  spawnSkeleton,
  logAction,
  updateTurn,
  applyCooldown,
  playSound,
  playDiceRollSound,
  resetSpecialCooldown,
  getIncome,
};
